import React, { useState } from 'react';
import Head from 'next/head';
import { Box, Grid } from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import HistoryIcon from '@mui/icons-material/History';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import InfoIcon from '@mui/icons-material/Info';
import BuildIcon from '@mui/icons-material/Build';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import PermContactCalendarIcon from '@mui/icons-material/PermContactCalendar';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';

import {
  PermissionsGate,
  TrafficOffensesDocumentModal,
  TabsV2,
  CommentsV2,
  ActivityListV2,
  TaskDetailScrollableSection,
  MoreButton,
  TaskSection,
  TaskCarDetails,
  TrafficOffensesForm,
  TrafficOffensesTaskDetails,
} from '@components';
import { UploadDocumentV3, UploadImageV3 } from 'components/common/media';
import { DrawerLayout, TaskLayout } from '@layouts';

import { useTrafficOffensesTaskOperations } from '@hooks';
import { GENERIC_ERROR_MESSAGE, SCOPES } from '@constants';

import { asyncForEach, showError } from '@utils';
import { boardsServices } from '@services';
import { ButtonV2 } from 'components/common/ui';
import {
  TRAFFIC_OFFENSES_TASK_FIELD_NAMES,
  TRAFFIC_OFFENSES_TASK_STAGES,
  TRAFFIC_OFFENSES_TASK_STAGES_MAP,
} from 'components/trafficOffenses/constants';
import { URLS } from 'layouts/DrawerLayout/constants';
import { getTrafficOffensesTaskPayload } from 'hooks/trafficOffenses/utils';

export const tabs = [
  {
    label: 'Ticket Information',
    id: 'ticket-information',
    icon: <InfoIcon data-testid="InfoIcon" />,
  },
  {
    label: 'Authority',
    id: 'authority',
    icon: <PermContactCalendarIcon data-testid="PermContactCalendarIcon" />,
  },
  {
    label: 'Work order',
    id: 'work-order',
    icon: <BuildIcon data-testid="BuildIcon" />,
  },
  {
    label: 'Comments',
    id: 'comments',
    icon: <ChatBubbleIcon data-testid="ChatBubbleIcon" />,
  },
  {
    label: 'History',
    id: 'history',
    icon: <HistoryIcon data-testid="HistoryIcon" />,
  },
];

export const rightTabs = [
  {
    icon: <FormatListBulletedIcon data-testid="FormatListBulletedIcon" />,
    id: 'ticket-details-right',
    label: 'Ticket Details',
  },
  {
    icon: <DirectionsCarIcon data-testid="DirectionsCarIcon" />,
    id: 'car-details',
    label: 'Car Details',
  },
  {
    icon: <InsertDriveFileIcon data-testid="InsertDriveFileIcon" />,
    id: 'documents',
    label: 'Documents',
  },
  {
    icon: <InsertPhotoIcon data-testid="InsertPhotoIcon" />,
    id: 'images',
    label: 'Photos',
  },
];

const TrafficOffensesTaskId = () => {
  const [submitting, setSubmitting] = useState(false);
  const {
    task,
    vehicle,
    taskId,
    loading,
    stages,
    agents,
    assignees,
    getTrafficOffensesTask,
    updateTask,
  } = useTrafficOffensesTaskOperations();

  const handleDeleteDocument = async (docId) => {
    try {
      setSubmitting(true);
      await boardsServices.deleteTaskDocument(taskId, docId);
      await getTrafficOffensesTask();
    } catch (error) {
      showError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangeStatus = async (toStatus, fromStatus) => {
    if (
      !task.customFields?.dateOfCompletion &&
      ((fromStatus === TRAFFIC_OFFENSES_TASK_STAGES_MAP.inProgress.value &&
        toStatus === TRAFFIC_OFFENSES_TASK_STAGES_MAP.done.value) ||
        (fromStatus === TRAFFIC_OFFENSES_TASK_STAGES_MAP.billing.value &&
          toStatus === TRAFFIC_OFFENSES_TASK_STAGES_MAP.done.value))
    ) {
      showError('Date of Completion is missing');
      return;
    }
    try {
      await updateTask({
        id: taskId,
        status: toStatus,
      });
      await getTrafficOffensesTask();
    } catch (error) {
      showError(error || GENERIC_ERROR_MESSAGE);
    }
  };

  const handleChangeTaskDetails = async ({ field, value }) => {
    try {
      setSubmitting(true);
      const payload = {
        id: taskId,
        [field]: value,
      };
      await updateTask(payload);
      await getTrafficOffensesTask();
    } catch (error) {
      showError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);
      const payload = getTrafficOffensesTaskPayload(values);

      await updateTask({
        id: taskId,
        ...payload,
      });

      await getTrafficOffensesTask();
    } catch (error) {
      showError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddComment = async (comment) => {
    try {
      setSubmitting(true);
      await boardsServices.addTaskComment(taskId, comment);
      await getTrafficOffensesTask();
    } catch (error) {
      showError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditComment = async (commentId, comment) => {
    try {
      setSubmitting(true);
      await boardsServices.editTaskComment(taskId, commentId, comment);
      await getTrafficOffensesTask();
    } catch (error) {
      showError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      setSubmitting(true);
      await boardsServices.deleteTaskComment(taskId, commentId);
      await getTrafficOffensesTask();
    } catch (error) {
      showError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    try {
      setSubmitting(true);
      await boardsServices.deleteTaskImage(taskId, imageId);
      await getTrafficOffensesTask();
    } catch (error) {
      showError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitDocuments = async (docs) => {
    try {
      setSubmitting(true);
      const { documents = [], documentType } = docs;

      await asyncForEach(documents, async (doc) => {
        await boardsServices.uploadTaskDocument(
          taskId,
          doc,
          documentType || ''
        );
      });

      await getTrafficOffensesTask();
    } catch (error) {
      showError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveImage = async (images = []) => {
    if (!images.length) return;
    try {
      setSubmitting(true);
      await asyncForEach(images, async (img) => {
        await boardsServices.uploadTaskImage(taskId, img?.file);
      });
      await getTrafficOffensesTask();
    } catch (error) {
      showError(error);
    } finally {
      setSubmitting(false);
    }
  };
  const renderHeaderOptions = () => {
    const getMoreOptions = () => {
      if (
        task?.status?.value === TRAFFIC_OFFENSES_TASK_STAGES_MAP.cancelled.value
      ) {
        return undefined;
      }
      const options = [
        {
          title: 'Cancelled',
          onClick: () =>
            handleChangeStatus(
              TRAFFIC_OFFENSES_TASK_STAGES_MAP.cancelled.value
            ),
          scopes: [SCOPES.canEdit],
        },
      ];
      if (
        task?.status?.value === TRAFFIC_OFFENSES_TASK_STAGES_MAP.backlog.value
      ) {
        options.push({
          title: 'Documents Acquired',
          onClick: () =>
            handleChangeStatus(
              TRAFFIC_OFFENSES_TASK_STAGES_MAP.inProgress.value
            ),
          scopes: [SCOPES.canEdit],
        });
      }
      if (
        task?.status?.value ===
        TRAFFIC_OFFENSES_TASK_STAGES_MAP.inProgress.value
      ) {
        options.push({
          title: 'For Billing',

          onClick: () =>
            handleChangeStatus(TRAFFIC_OFFENSES_TASK_STAGES_MAP.billing.value),
          scopes: [SCOPES.canEdit],
        });
        options.push({
          title: 'Done',

          onClick: () => {
            handleChangeStatus(
              TRAFFIC_OFFENSES_TASK_STAGES_MAP.done.value,
              TRAFFIC_OFFENSES_TASK_STAGES_MAP.inProgress.value
            );
          },
          scopes: [SCOPES.canEdit],
        });
      }
      if (
        task?.status?.value === TRAFFIC_OFFENSES_TASK_STAGES_MAP.billing.value
      ) {
        options.push({
          title: 'Done',
          onClick: () => {
            handleChangeStatus(
              TRAFFIC_OFFENSES_TASK_STAGES_MAP.done.value,
              TRAFFIC_OFFENSES_TASK_STAGES_MAP.billing.value
            );
          },
          scopes: [SCOPES.canEdit],
        });
      }

      return options;
    };

    return (
      <PermissionsGate>
        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <MoreButton moreOptions={getMoreOptions()} />
          <ButtonV2
            type="submit"
            form="traffic-offence-form"
            disabled={loading || submitting}
          >
            Save
          </ButtonV2>
        </Box>
      </PermissionsGate>
    );
  };

  const taskStatusNames = Object.keys(TRAFFIC_OFFENSES_TASK_STAGES).reduce(
    (acc, cur) => {
      acc[cur] = TRAFFIC_OFFENSES_TASK_STAGES[cur].label;

      return acc;
    },
    {}
  );

  return (
    <>
      <Head>
        <title>{`Traffic Offenses/Traffic Offense: ${taskId} `}</title>
        <meta name="description" content="Instacar ledger" />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <TaskLayout
        isLoading={loading || submitting}
        task={taskId}
        url={URLS.trafficOffenses}
        renderHeaderOptions={renderHeaderOptions}
        scopes={[SCOPES.canView]}
        stages={stages}
        currentStage={task?.status?.value}
      >
        <Grid container alignItems="start" spacing={1}>
          <Grid size={{ xs: 12, md: 8 }}>
            <TabsV2
              tabs={tabs}
              observerRootId="traffic-offenses-task-details"
            />

            <TaskDetailScrollableSection id="traffic-offenses-task-details">
              <TrafficOffensesForm
                onSubmit={handleSubmit}
                defaultValues={task}
                loading={loading || submitting}
                documents={[...(task?.documents || [])]}
                images={[...(task?.images || [])]}
                taskId={taskId}
                assignees={assignees || []}
              />
              <TaskSection
                title={tabs[3].label}
                id={tabs[3].id}
                icon={tabs[3].icon}
              >
                <CommentsV2
                  comments={task?.comments}
                  users={agents}
                  onSubmit={handleAddComment}
                  onEdit={handleEditComment}
                  onDelete={handleDeleteComment}
                />
              </TaskSection>
              <TaskSection
                title={tabs[4].label}
                id={tabs[4].id}
                icon={tabs[4].icon}
              >
                <ActivityListV2
                  activities={task?.changelog}
                  taskFieldNames={TRAFFIC_OFFENSES_TASK_FIELD_NAMES}
                  taskStatusNames={taskStatusNames}
                />
              </TaskSection>
            </TaskDetailScrollableSection>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TabsV2
              tabs={rightTabs}
              orientation="vertical"
              observerRootId="traffic-offenses-right-details"
            />
            <TaskDetailScrollableSection id="traffic-offenses-right-details">
              <TaskSection
                isSmall
                title={rightTabs[0].label}
                id={rightTabs[0].id}
                icon={rightTabs[0].icon}
              >
                <TrafficOffensesTaskDetails
                  task={task}
                  stages={stages}
                  assignees={assignees}
                  onChange={handleChangeTaskDetails}
                />
              </TaskSection>
              <TaskSection
                isSmall
                title={rightTabs[1].label}
                id={rightTabs[1].id}
                icon={rightTabs[1].icon}
              >
                <TaskCarDetails vehicle={vehicle} />
              </TaskSection>
              <TaskSection
                isSmall
                title={rightTabs[2].label}
                id={rightTabs[2].id}
                icon={rightTabs[2].icon}
              >
                <UploadDocumentV3
                  enableUpload={false}
                  documents={task?.documents}
                  onDeleteDocument={handleDeleteDocument}
                  loading={loading || submitting}
                  UploadComponent={
                    <TrafficOffensesDocumentModal
                      loading={loading || submitting}
                      onSubmit={handleSubmitDocuments}
                    />
                  }
                />
              </TaskSection>
              <TaskSection
                isSmall
                title={rightTabs[3].label}
                id={rightTabs[3].id}
                icon={rightTabs[3].icon}
              >
                <UploadImageV3
                  disableLocalFiles
                  shouldChangeOnDelete={false}
                  images={task?.images || []}
                  onChange={handleSaveImage}
                  onDeleteImage={handleDeleteImage}
                  loading={loading || submitting}
                />
              </TaskSection>
            </TaskDetailScrollableSection>
          </Grid>
        </Grid>
      </TaskLayout>
    </>
  );
};

TrafficOffensesTaskId.getLayout = (page) => {
  return <DrawerLayout>{page}</DrawerLayout>;
};

export default TrafficOffensesTaskId;
